import { isArrayOfFloatArray } from "../../types";
import { Cartesian2, Cartesian3 } from "../geometry.cartesian";
import { isArrayOfCartesianArray } from "../geometry.interfaces";
import { Polyline } from "./geometry.polyline";
import { ShapeType } from "./geometry.shapes.interfaces";
class Cartesian3WithInfos extends Cartesian3 {
    constructor(x, y, z, __wayCode__$t1967, subject, subjectIndex, clipIndex = 0) {
        super(x, y, z);
        this.__wayCode__$t1967 = __wayCode__$t1967;
        this.subject = subject;
        this.subjectIndex = subjectIndex;
        this.clipIndex = clipIndex;
    }
}
function isIntersection(p) {
    return p.__wayCode__$t1967 !== undefined;
}
export class Polygon extends Polyline {
    static IsClockWise(points) {
        let sum = 0;
        const n = points.length;
        for (let i = 0; i < points.length - 1; i++) {
            const a = points[i];
            const b = points[(i + 1) % n];
            sum += (b.x - a.x) * (b.y + a.y);
        }
        return sum < 0;
    }
    static FromFloats(points, stride = 3, assertClose = true, assertClockWize = true) {
        if (isArrayOfFloatArray(points)) {
            const records = [];
            for (let i = 0; i < points.length; i++) {
                const tmp = points[i];
                const p = [];
                for (let j = 0; j < tmp.length; j += stride) {
                    p.push(new Cartesian3(tmp[j], tmp[j + 1], stride > 2 ? tmp[j + 2] : 0));
                }
                records.push(p);
            }
            return Polygon.FromPoints(records, assertClose, assertClockWize);
        }
        const p = [];
        for (let i = 0; i < points.length; i += stride) {
            p.push(new Cartesian3(points[i], points[i + 1], stride > 2 ? points[i + 2] : 0));
        }
        return new Polygon(p, assertClose, assertClockWize);
    }
    static FromPoints(points, assertClose = true, assertClockWize = true) {
        if (isArrayOfCartesianArray(points)) {
            const p = new Polygon(points[0], assertClose, assertClockWize);
            for (let i = 1; i < points.length; i++) {
                p.addHole(points[i], assertClose, assertClockWize);
            }
            return p;
        }
        return new Polygon(points, assertClose, assertClockWize);
    }
    constructor(p, assertClose = true, assertClockWize = true) {
        super(p, ShapeType.Polygon);
        if (assertClose && !Cartesian3.Equals(p[0], p[p.length - 1])) {
            p.push(p[0]);
        }
        if (assertClockWize && !Polygon.IsClockWise(p)) {
            p.reverse();
        }
    }
    get holes() {
        return this._holes;
    }
    addHole(hole, assertClose = true, assertAntiClockWize = true) {
        if ((hole?.length ?? 0) > 2) {
            if (assertClose && !Cartesian3.Equals(hole[0], hole[hole.length - 1])) {
                hole.push(hole[0]);
            }
            if (assertAntiClockWize && Polygon.IsClockWise(hole)) {
                hole.reverse();
            }
            this._holes = this._holes ?? [];
            this._holes.push(hole);
        }
    }
    clip(clipArea) {
        if (clipArea.containsBounds(this.boundingBox)) {
            return this;
        }
        return this._clipPolygon(clipArea);
    }
    _clipPolygon(clipArea) {
        if (clipArea.intersects(this.boundingBox)) {
            const polygonArea = Array.from(clipArea.points()).map((p) => this._buildPoint(p.x, p.y, 0));
            polygonArea.push(polygonArea[0]);
            const toClips = [this._points];
            if (this._holes) {
                toClips.push(...this._holes);
            }
            const entering = [];
            for (const subject of toClips) {
                const points = [];
                const codes = subject.map((p) => Cartesian2.ComputeCode(p, clipArea));
                let a = subject[0];
                let codea = codes[0];
                let inside = codea == 0;
                for (let i = 1; i < codes.length; i++) {
                    const b = subject[i];
                    const codeb = codes[i];
                    if (inside) {
                        points.push(this._buildPoint(a.x, a.y, a.z));
                        if (codeb != 0) {
                            const intersection = this._computeIntersectionToRef(clipArea, a, b, codeb, new Cartesian3WithInfos(0, 0, 0, false, points, points.length));
                            points.push(intersection);
                            intersection.clipIndex = this._InsertIntersectionIntoClipPolygon(polygonArea, intersection);
                            inside = !inside;
                        }
                    }
                    else {
                        points.push(a);
                        if (codeb == 0) {
                            const intersection = this._computeIntersectionToRef(clipArea, a, b, codea, new Cartesian3WithInfos(0, 0, 0, true, points, points.length));
                            entering.push(intersection);
                            points.push(intersection);
                            intersection.clipIndex = this._InsertIntersectionIntoClipPolygon(polygonArea, intersection);
                            inside = !inside;
                        }
                    }
                    a = b;
                    codea = codeb;
                }
                if (inside) {
                    points.push(this._buildPoint(a.x, a.y, a.z));
                }
            }
            const polygons = [];
            if (entering.length != 0) {
                do {
                    const polygon = [];
                    const first = entering.splice(0, 1)[0];
                    let i = first.subjectIndex;
                    let subject = first.subject;
                    do {
                        const current = subject[i++];
                        i == subject.length && (i = 1);
                        if (isIntersection(current)) {
                            polygon.push(this._buildPoint(current.x, current.y, current.z));
                            if (!current.__wayCode__$t1967) {
                                i = current.clipIndex + 1;
                                subject = polygonArea;
                                continue;
                            }
                            if (polygon.length == 1) {
                                continue;
                            }
                            if (current === first) {
                                polygons.push(polygon);
                                break;
                            }
                            i = current.subjectIndex + 1;
                            subject = current.subject;
                            entering.splice(entering.findIndex((p) => p === current), 1);
                            continue;
                        }
                        polygon.push(current);
                    } while (true);
                } while (entering.length > 0);
            }
            return polygons.length ? (polygons.length == 1 ? new Polygon(polygons[0], false, false) : polygons.map((p) => new Polygon(p, false, false))) : undefined;
        }
        return undefined;
    }
    _InsertIntersectionIntoClipPolygon(clipPolygon, intersection) {
        let a = clipPolygon[0];
        for (let i = 1; i < clipPolygon.length; i++) {
            const b = clipPolygon[i];
            if (Cartesian3.IsWithinTheBounds(a, b, intersection)) {
                clipPolygon.splice(i, 0, intersection);
                for (let j = i + 1; j < clipPolygon.length; j++) {
                    const c = clipPolygon[j];
                    if (isIntersection(c)) {
                        c.clipIndex++;
                    }
                }
                return i;
            }
            a = b;
        }
        return -1;
    }
}
//# sourceMappingURL=geometry.polygon.js.map